import React, { useState, useEffect, useCallback } from 'react';
import { logger } from '@/utils/logger';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getBookingApiUrl } from '@/config/api.config';
import {
  MapPin,
  Plus,
  Search,
  Edit3,
  Trash2,
  Clock,
  Settings,
  Users,
  Building,
  RefreshCw,
  X,
  Save,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import { getNzDateString } from '@/utils/nzDate';

// Types based on booking-api structure
interface Location {
  id: number;
  name: string;
  address?: string;
  phone?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Court {
  id: number;
  location_id: number;
  name: string;
  type: string;
  surface: string;
  facilities?: string | string[] | null;
  hourly_rate: number;
  member_rate?: number;
  max_capacity: number;
  is_active: boolean;
  location_name?: string;
  created_at: string;
  updated_at: string;
}

interface CourtFormData {
  location_id: number;
  name: string;
  type: 'indoor' | 'outdoor';
  surface: string;
  facilities: string;
  hourly_rate: number;
  member_rate: number;
  max_capacity: number;
}

type TimeSlotStatus = 'available' | 'unavailable' | 'maintenance' | 'holiday';

interface CourtTimeSlotSummary {
  status?: TimeSlotStatus;
}

const addDaysToDateString = (dateString: string, days: number): string => {
  const [year, month, day] = dateString.split('-').map(Number);
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  utcDate.setUTCDate(utcDate.getUTCDate() + days);
  return utcDate.toISOString().slice(0, 10);
};

const CourtManagement: React.FC = () => {
  const { t } = useTranslation();

  const parseFacilities = (value: Court['facilities']): string[] => {
    if (Array.isArray(value)) {
      return value.map((item) => String(item).trim()).filter(Boolean);
    }

    if (typeof value !== 'string') return [];

    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      // Fallback: facilities may be a plain comma-separated string.
    }

    return trimmed
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  };

  // State管理
  const [locations, setLocations] = useState<Location[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [courtStatus, setCourtStatus] = useState<Record<number, {
    availableSlots: number;
    disabledSlots: number;
    totalSlots: number;
    hasDisabledSlots: boolean;
  }>>({});
  const [selectedLocationId, setSelectedLocationId] = useState<number | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateCourtModal, setShowCreateCourtModal] = useState(false);
  const [showEditCourtModal, setShowEditCourtModal] = useState(false);
  const [showTimeSlotModal, setShowTimeSlotModal] = useState(false);
  const [saveNotice, setSaveNotice] = useState<string | null>(null);
  const [editingCourt, setEditingCourt] = useState<Court | null>(null);
  const [managingTimeSlotCourt, setManagingTimeSlotCourt] = useState<Court | null>(null);

  // 表单数据
  const [courtFormData, setCourtFormData] = useState<CourtFormData>({
    location_id: 1,
    name: '',
    type: 'indoor',
    surface: 'wood',
    facilities: '',
    hourly_rate: 60,
    member_rate: 50,
    max_capacity: 4
  });

  // Fetch venue list
  const fetchLocations = useCallback(async () => {
    try {
      // 场馆列表API不需要认证
      const response = await fetch(getBookingApiUrl('courts/locations'));
      
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
      } else {
        logger.error(t('venue.errors.fetchVenuesFailed'), response.status);
        toast.error(t('venue.errors.fetchVenuesFailed'));
      }
    } catch (error) {
      logger.error(t('venue.errors.fetchVenuesFailed'), error);
      toast.error(t('venue.errors.fetchVenuesFailed'));
    }
  }, [t]);

  // 获取所有Courts的状态（并行请求，带超时保护）
  const fetchCourtsStatus = useCallback(async (courtsToCheck: Court[]) => {
    const startDate = getNzDateString();
    const endDate = addDaysToDateString(startDate, 14);
    const statusMap: Record<number, {
      availableSlots: number;
      disabledSlots: number;
      totalSlots: number;
      hasDisabledSlots: boolean;
    }> = {};

    const fetchPromises = courtsToCheck.map(async (court) => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5秒超时

        const response = await fetch(
          getBookingApiUrl(`courts/${court.id}/time-slots?start_date=${startDate}&end_date=${endDate}`),
          {
          signal: controller.signal
          }
        );

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = (await response.json()) as { timeSlots?: CourtTimeSlotSummary[] };
          const timeSlots = Array.isArray(data.timeSlots) ? data.timeSlots : [];

          const disabledSlots = timeSlots.filter((slot) => slot.status === 'unavailable').length;
          const availableSlots = timeSlots.filter((slot) => slot.status === 'available').length;

          statusMap[court.id] = {
            totalSlots: timeSlots.length,
            disabledSlots,
            availableSlots,
            hasDisabledSlots: disabledSlots > 0
          };
        } else {
          statusMap[court.id] = {
            totalSlots: 0,
            disabledSlots: 0,
            availableSlots: 0,
            hasDisabledSlots: false
          };
        }
      } catch (error) {
        logger.error(`Failed to fetch status for court ${court.id}:`, error);
        statusMap[court.id] = {
          totalSlots: 0,
          disabledSlots: 0,
          availableSlots: 0,
          hasDisabledSlots: false
        };
      }
    });

    await Promise.allSettled(fetchPromises);
    setCourtStatus(statusMap);
  }, []);

  // Fetch court list
  const fetchCourts = useCallback(async () => {
    setIsLoading(true);
    try {
      // 场地列表API也不需要认证
      const url = selectedLocationId
        ? getBookingApiUrl(`courts?location_id=${selectedLocationId}`)
        : getBookingApiUrl('courts');
        
      const response = await fetch(url);
      
      if (response.ok) {
        const data = await response.json();
        setCourts(data);
        logger.info(t('venue.success.courtsDataReceived'), data);
        // Render courts first, then hydrate slot status asynchronously
        void fetchCourtsStatus(data);
      } else {
        logger.error(t('venue.errors.fetchCourtsFailed'), response.status);
        toast.error(t('venue.errors.fetchCourtsFailed'));
      }
    } catch (error) {
      logger.error(t('venue.errors.fetchCourtsFailed'), error);
      toast.error(t('venue.errors.fetchCourtsFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [fetchCourtsStatus, selectedLocationId, t]);

  // 创建场地
  const handleCreateCourt = async () => {
    if (!courtFormData.location_id || !courtFormData.name || !courtFormData.hourly_rate) {
      toast.error(t('courts.courtNameRequired'));
      return;
    }

    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(getBookingApiUrl('courts'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...courtFormData,
          facilities: courtFormData.facilities ? courtFormData.facilities.split(',').map(f => f.trim()) : null
        })
      });

      if (response.ok) {
        toast.success(`${t('courts.createCourtSuccess')} (Saved successfully)`);
        setSaveNotice('Saved successfully');
        setShowCreateCourtModal(false);
        resetForm();
        fetchCourts();
      } else {
        const error = await response.json();
        toast.error(error.error || t('courts.createCourtFailed'));
      }
    } catch (error) {
      logger.error(t('venue.errors.createCourtFailed'), error);
      toast.error(t('courts.createCourtFailed'));
    }
  };

  // 编辑场地
  const handleEditCourt = async () => {
    if (!editingCourt || !courtFormData.name || !courtFormData.hourly_rate) {
      toast.error(t('courts.courtNameRequired'));
      return;
    }

    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(getBookingApiUrl(`courts/${editingCourt.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...courtFormData,
          facilities: courtFormData.facilities ? courtFormData.facilities.split(',').map(f => f.trim()) : null
        })
      });

      if (response.ok) {
        toast.success(`${t('courts.updateCourtSuccess')} (Updated successfully)`);
        setSaveNotice('Updated successfully');
        setShowEditCourtModal(false);
        setEditingCourt(null);
        resetForm();
        fetchCourts();
      } else {
        const error = await response.json();
        toast.error(error.error || t('courts.updateCourtFailed'));
      }
    } catch (error) {
      logger.error(t('venue.errors.updateCourtFailed'), error);
      toast.error(t('courts.updateCourtFailed'));
    }
  };

  // 删除场地
  const handleDeleteCourt = async (courtId: number, courtName: string) => {
    if (!confirm(t('courts.confirmDelete', { courtName }))) {
      return;
    }

    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(getBookingApiUrl(`courts/${courtId}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        toast.success(t('courts.deleteCourtSuccess'));
        setSaveNotice('Deleted successfully');
        fetchCourts();
      } else {
        const error = await response.json();
        toast.error(error.error || t('courts.deleteCourtFailed'));
      }
    } catch (error) {
      logger.error(t('venue.errors.deleteCourtFailed'), error);
      toast.error(t('courts.deleteCourtFailed'));
    }
  };

  // 重置表单
  const resetForm = useCallback(() => {
    setCourtFormData({
      location_id: locations.length > 0 ? locations[0].id : 1,
      name: '',
      type: 'indoor',
      surface: 'wood',
      facilities: '',
      hourly_rate: 60,
      member_rate: 50,
      max_capacity: 4
    });
  }, [locations]);

  // 优化输入处理
  const handleFormDataChange = useCallback(<K extends keyof CourtFormData>(field: K, value: CourtFormData[K]) => {
    setCourtFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // 打开编辑模态框
  const toCourtType = (value: string): CourtFormData['type'] => (value === 'outdoor' ? 'outdoor' : 'indoor');

  const openEditModal = (court: Court) => {
    setEditingCourt(court);
    setCourtFormData({
      location_id: court.location_id,
      name: court.name,
      type: toCourtType(court.type),
      surface: court.surface,
      facilities: parseFacilities(court.facilities).join(', '),
      hourly_rate: court.hourly_rate,
      member_rate: court.member_rate || 0,
      max_capacity: court.max_capacity
    });
    setShowEditCourtModal(true);
  };

  // 过滤场地
  const filteredCourts = courts.filter(court =>
    court.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (court.location_name && court.location_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // 初始化数据
  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    fetchCourts();
  }, [fetchCourts]);

  // 当有场馆数据时，确保表单有正确的默认场馆ID
  useEffect(() => {
    if (locations.length > 0 && courtFormData.location_id === 1 && locations.findIndex(l => l.id === 1) === -1) {
      setCourtFormData(prev => ({
        ...prev,
        location_id: locations[0].id
      }));
    }
  }, [locations, courtFormData.location_id]);

  // 表单模态框组件
  const CourtFormModal = ({ isEdit = false }: { isEdit?: boolean }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-hard w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEdit ? t('courts.editCourt') : t('courts.createCourt')}
            </h3>
            <button
              onClick={() => {
                if (isEdit) {
                  setShowEditCourtModal(false);
                  setEditingCourt(null);
                } else {
                  setShowCreateCourtModal(false);
                }
                resetForm();
              }}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Venue selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.venue')} <span className="text-red-500">*</span>
              </label>
              <select
                value={courtFormData.location_id}
                onChange={(e) => setCourtFormData(prev => ({ ...prev, location_id: Number(e.target.value) }))}
                className="w-full input"
              >
                {locations.map(location => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Court name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.courtName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={courtFormData.name}
                onChange={(e) => handleFormDataChange('name', e.target.value)}
                placeholder={t('venue.placeholders.courtName')}
                className="w-full input"
              />
            </div>

            {/* Court type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.courtType')}
              </label>
              <select
                value={courtFormData.type}
                onChange={(e) =>
                  setCourtFormData(prev => ({ ...prev, type: toCourtType(e.target.value) }))
                }
                className="w-full input"
              >
                <option value="indoor">{t('courts.indoorType')}</option>
                <option value="outdoor">{t('courts.outdoorType')}</option>
              </select>
            </div>

            {/* Floor material */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.surfaceMaterial')}
              </label>
              <select
                value={courtFormData.surface}
                onChange={(e) => setCourtFormData(prev => ({ ...prev, surface: e.target.value }))}
                className="w-full input"
              >
                <option value="wood">{t('courts.woodSurface')}</option>
                <option value="plastic">{t('courts.plasticSurface')}</option>
                <option value="concrete">{t('courts.concreteSurface')}</option>
                <option value="carpet">{t('courts.carpetSurface')}</option>
              </select>
            </div>

            {/* 设施 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.facilities')}
              </label>
              <input
                type="text"
                value={courtFormData.facilities}
                onChange={(e) => setCourtFormData(prev => ({ ...prev, facilities: e.target.value }))}
                placeholder={t('venue.placeholders.facilities')}
                className="w-full input"
              />
            </div>

            {/* Hourly rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.hourlyRateLabel')} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={courtFormData.hourly_rate}
                onChange={(e) => setCourtFormData(prev => ({ ...prev, hourly_rate: Number(e.target.value) }))}
                placeholder="80"
                min="0"
                step="1"
                className="w-full input"
              />
            </div>

            {/* 会员费率 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.memberRateOptional')}
              </label>
              <input
                type="number"
                value={courtFormData.member_rate}
                onChange={(e) => setCourtFormData(prev => ({ ...prev, member_rate: Number(e.target.value) }))}
                placeholder="60"
                min="0"
                step="1"
                className="w-full input"
              />
            </div>

            {/* 最大容量 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courts.maxCapacityLabel')}
              </label>
              <input
                type="number"
                value={courtFormData.max_capacity}
                onChange={(e) => setCourtFormData(prev => ({ ...prev, max_capacity: Number(e.target.value) }))}
                min="1"
                max="10"
                className="w-full input"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              onClick={() => {
                if (isEdit) {
                  setShowEditCourtModal(false);
                  setEditingCourt(null);
                } else {
                  setShowCreateCourtModal(false);
                }
                resetForm();
              }}
              className="flex-1 btn btn-secondary"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={isEdit ? handleEditCourt : handleCreateCourt}
              className="flex-1 btn btn-primary gap-2"
            >
              <Save className="h-4 w-4" />
              {isEdit ? `${t('common.save')} / Save` : t('common.add')}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <MapPin className="h-7 w-7 text-primary-600" />
            {t('courts.title')}
          </h1>
          <p className="text-gray-500 mt-1">
            {t('courts.description')}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={fetchCourts}
            disabled={isLoading}
            className="btn btn-secondary gap-2"
          >
            <RefreshCw className={clsx("h-4 w-4", isLoading && "animate-spin")} />
            {t('common.refresh')}
          </button>
          <button
            onClick={() => {
              resetForm();
              setShowCreateCourtModal(true);
            }}
            className="btn btn-primary gap-2"
          >
            <Plus className="h-4 w-4" />
            {t('courts.createCourt')}
          </button>
        </div>
      </div>

      {saveNotice && (
        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-800">{saveNotice}</span>
        </div>
      )}

      {/* Filters */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* 场馆筛选 */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('courts.venue')} {t('common.filter')}
            </label>
            <select
              value={selectedLocationId || ''}
              onChange={(e) => setSelectedLocationId(e.target.value ? Number(e.target.value) : undefined)}
              className="w-full input"
            >
              <option value="">{t('common.all')} {t('courts.venue')}</option>
              {locations.map(location => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </div>

          {/* 搜索 */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('common.search')} {t('courts.title').split(' ')[0]}
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('courts.searchPlaceholder')}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </div>

        {/* 统计信息 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-200">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{locations.length}</div>
            <div className="text-sm text-gray-600">{t('courts.totalVenues')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{courts.length}</div>
            <div className="text-sm text-gray-600">{t('courts.totalCourts')}</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900">{courts.filter(c => c.is_active).length}</div>
            <div className="text-sm text-gray-600">{t('courts.activeCourts')}</div>
          </div>
        </div>
      </div>

      {/* Courts Grid */}
      {isLoading ? (
        <div className="card p-8">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary-600 mb-4" />
            <p className="text-gray-600">{t('courts.loadingData')}</p>
          </div>
        </div>
      ) : filteredCourts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourts.map((court) => (
            <motion.div
              key={court.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card hover:shadow-medium transition-shadow"
            >
              <div className="p-6">
                {/* Court Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                      {court.name}
                      {!court.is_active && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                          {t('courts.inactive')}
                        </span>
                      )}
                      {courtStatus[court.id]?.hasDisabledSlots && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          {t('courts.partiallyDisabled')}
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                      <Building className="h-4 w-4" />
                      {court.location_name}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => {
                        setManagingTimeSlotCourt(court);
                        setShowTimeSlotModal(true);
                      }}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title={t('timeSlots.manageTimeSlots')}
                      aria-label="Manage Time Slots"
                    >
                      <Clock className="h-4 w-4" />
                      <span className="sr-only">Manage Time Slots</span>
                    </button>
                    <button
                      onClick={() => openEditModal(court)}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Edit"
                      aria-label="Edit"
                    >
                      <Edit3 className="h-4 w-4" />
                      <span className="text-xs font-medium leading-none">Edit</span>
                    </button>
                    <button
                      onClick={() => handleDeleteCourt(court.id, court.name)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete</span>
                    </button>
                  </div>
                </div>

                {/* Court Info */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('courts.typeLabel')}</span>
                    <span className="font-medium">
                      {court.type === 'indoor' ? t('venue.courtType.indoor') : t('venue.courtType.outdoor')} - {court.surface}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('courts.priceLabel')}</span>
                    <span className="font-medium text-success-600">
                      ${court.hourly_rate}/{t('courts.hourLabel')}
                      {court.member_rate && court.member_rate > 0 && (
                        <span className="text-gray-500 ml-1">
                          ({t('courts.memberLabel')} ${court.member_rate})
                        </span>
                      )}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('courts.capacityLabel')}</span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {court.max_capacity}{t('courts.peopleUnit')}
                    </span>
                  </div>

                  {/* 状态统计 */}
                  {courtStatus[court.id] && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{t('courts.timeSlotsStatus')}</span>
                      <div className="flex items-center gap-2 text-xs">
                        {courtStatus[court.id].disabledSlots > 0 && (
                          <span className="text-orange-600">
                            {courtStatus[court.id].disabledSlots}{t('courts.disabled')}
                          </span>
                        )}
                        <span className="text-success-600">
                          {courtStatus[court.id].availableSlots}{t('courts.available')}
                        </span>
                        <span className="text-gray-500">
                          /{t('courts.totalSlots', { count: courtStatus[court.id].totalSlots })}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {court.facilities && (
                    <div className="mt-3">
                      <div className="text-xs text-gray-600 mb-2">{t('courts.facilitiesLabel')}</div>
                      <div className="flex flex-wrap gap-1">
                        {parseFacilities(court.facilities).map((facility, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-primary-50 text-primary-700 text-xs rounded-full"
                          >
                            {facility}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Status Indicator */}
                <div className={clsx(
                  "mt-4 pt-4 border-t border-gray-200 flex items-center gap-2 text-sm",
                  court.is_active ? "text-success-600" : "text-red-600"
                )}>
                  {court.is_active ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  {court.is_active ? t('venue.status.active') : t('venue.status.inactive')}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="card p-8">
          <div className="text-center">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('courts.noCourtsYet')}</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm ? t('venue.messages.noMatchingCourts') : t('venue.messages.noCoursCreated')}
            </p>
            {!searchTerm && (
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateCourtModal(true);
                }}
                className="btn btn-primary gap-2 mx-auto"
              >
                <Plus className="h-4 w-4" />
                {t('courts.createFirstCourt')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showCreateCourtModal && <CourtFormModal />}
        {showEditCourtModal && <CourtFormModal isEdit />}
        {showTimeSlotModal && (
          <TimeSlotManagementModal
            court={managingTimeSlotCourt}
            onClose={() => {
              setShowTimeSlotModal(false);
              setManagingTimeSlotCourt(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// 时间段接口定义
interface TimeSlot {
  id: number;
  court_id: number;
  date: string;
  start_time: string;
  end_time: string;
  base_price: number;
  member_price?: number;
  max_capacity: number;
  is_active: boolean;
  status: 'available' | 'unavailable' | 'maintenance' | 'holiday';
  unavailable_reason?: string;
  unavailable_until?: string;
  created_by?: string;
  metadata?: Record<string, unknown>;
}

// 时间段管理模态框组件
type OperationMode = 'view' | 'disable_all' | 'holiday' | 'maintenance' | 'specific';
type BatchOperationMode = Exclude<OperationMode, 'view'>;

interface BatchOperationPayload {
  action: BatchOperationMode;
  reason: string;
  status: 'unavailable';
  created_by: string;
  date_range?: [string, string];
  time_range?: { start: string; end: string };
}

const TimeSlotManagementModal = ({
  court,
  onClose
}: {
  court?: Court | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  // State管理
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>(getNzDateString());
  const [operationMode, setOperationMode] = useState<OperationMode>('view');

  // 快速操作表单数据
  const [quickActionData, setQuickActionData] = useState({
    reason: '',
    startDate: getNzDateString(),
    endDate: getNzDateString(),
    startTime: '08:00',
    endTime: '22:00'
  });

  // 获取时间段数据
  const courtId = court?.id;

  const fetchTimeSlots = useCallback(async () => {
    if (!courtId) return;

    setIsLoading(true);
    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(getBookingApiUrl(`courts/${courtId}/time-slots?date=${selectedDate}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        // API返回的是 {courtId, timeSlots, total} 结构，我们只需要timeSlots
        const timeSlotList = data.timeSlots || [];
        logger.info('Fetched time slots:', timeSlotList.length, 'slots');
        setTimeSlots(timeSlotList);
      } else {
        toast.error(t('timeSlots.loadFailed'));
      }
    } catch (error) {
      logger.error('获取时间段失败：', error);
      toast.error(t('timeSlots.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [courtId, selectedDate, t]);

  // 执行批量操作
  const executeQuickAction = async () => {
    if (!court || !quickActionData.reason.trim()) {
      toast.error(t('timeSlots.reasonRequired'));
      return;
    }

    try {
      // 从localStorage获取token，保持与其他函数一致
      const currentToken = localStorage.getItem('staff_token');
      logger.info('Token in executeQuickAction:', currentToken);
      if (!currentToken) {
        toast.error(t('auth.loginRequired'));
        return;
      }

      if (operationMode === 'view') {
        toast.error(t('timeSlots.selectOperation'));
        return;
      }

      const actionMode: BatchOperationMode = operationMode;
      let operationData: BatchOperationPayload = {
        action: actionMode,
        reason: quickActionData.reason,
        status: 'unavailable',
        created_by: 'admin'
      };

      if (actionMode === 'disable_all') {
        // 获取当前选择的日期或默认为今天
        const targetDate = selectedDate || getNzDateString();
        operationData = {
          ...operationData,
          date_range: [targetDate, targetDate]
        };
      } else if (actionMode === 'holiday' || actionMode === 'maintenance') {
        operationData = {
          ...operationData,
          date_range: [quickActionData.startDate, quickActionData.endDate],
          time_range: {
            start: quickActionData.startTime,
            end: quickActionData.endTime
          }
        };
      }

      const response = await fetch(getBookingApiUrl(`courts/${court.id}/time-slots/batch-update`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(operationData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(t('timeSlots.operationSuccess', { count: result.affected_count }));
        setOperationMode('view');
        setQuickActionData({
          reason: '',
          startDate: getNzDateString(),
          endDate: getNzDateString(),
          startTime: '08:00',
          endTime: '22:00'
        });
        fetchTimeSlots();
      } else {
        const error = await response.json();
        toast.error(error.message || t('timeSlots.operationFailed'));
      }
    } catch (error) {
      logger.error('批量操作失败：', error);
      toast.error(t('timeSlots.operationFailed'));
    }
  };

  // 切换单个时间段状态
  const toggleTimeSlotStatus = async (timeSlot: TimeSlot) => {
    const newStatus = timeSlot.status === 'available' ? 'unavailable' : 'available';
    const reason = newStatus === 'unavailable' ?
      prompt(t('timeSlots.enterReason')) : null;

    if (newStatus === 'unavailable' && !reason?.trim()) {
      return;
    }

    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(getBookingApiUrl(`time-slots/${timeSlot.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: newStatus,
          unavailable_reason: reason,
          created_by: 'admin'
        })
      });

      if (response.ok) {
        toast.success(t('timeSlots.statusUpdated'));
        fetchTimeSlots();
      } else {
        toast.error(t('timeSlots.statusUpdateFailed'));
      }
    } catch (error) {
      logger.error('更新时间段状态失败：', error);
      toast.error(t('timeSlots.statusUpdateFailed'));
    }
  };

  // 恢复可用状态
  const restoreAvailability = async () => {
    if (!court || !confirm(t('timeSlots.confirmRestore'))) {
      return;
    }

    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(getBookingApiUrl(`courts/${court.id}/time-slots/restore`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          created_by: 'admin'
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(t('timeSlots.restoreSuccess', { count: result.restored_count }));
        fetchTimeSlots();
      } else {
        toast.error(t('timeSlots.restoreFailed'));
      }
    } catch (error) {
      logger.error('恢复失败：', error);
      toast.error(t('timeSlots.restoreFailed'));
    }
  };

  // 初始加载
  useEffect(() => {
    fetchTimeSlots();
  }, [fetchTimeSlots]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-success-100 text-success-800';
      case 'unavailable': return 'bg-red-100 text-red-800';
      case 'maintenance': return 'bg-orange-100 text-orange-800';
      case 'holiday': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-xl shadow-hard w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {t('timeSlots.title')}
              </h3>
              {court && (
                <p className="text-sm text-gray-500 mt-1">
                  {court.name} - {court.location_name}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={restoreAvailability}
                className="px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 transition-colors flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                {t('timeSlots.restoreAll')}
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* 业务操作面板 */}
          <div className="bg-primary-50 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              {t('timeSlots.businessOperations')}
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <button
                onClick={() => setOperationMode('view')}
                className={clsx(
                  "p-3 rounded-lg border transition-colors text-left",
                  operationMode === 'view'
                    ? "bg-white border-blue-500 text-blue-700"
                    : "bg-white border-gray-200 hover:border-gray-300"
                )}
              >
                <div className="font-medium">{t('timeSlots.viewMode')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('timeSlots.viewModeDesc')}</div>
              </button>

              <button
                onClick={() => setOperationMode('disable_all')}
                className={clsx(
                  "p-3 rounded-lg border transition-colors text-left",
                  operationMode === 'disable_all'
                    ? "bg-white border-red-500 text-red-700"
                    : "bg-white border-gray-200 hover:border-gray-300"
                )}
              >
                <div className="font-medium">{t('timeSlots.disableAll')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('timeSlots.disableAllDesc')}</div>
              </button>

              <button
                onClick={() => setOperationMode('holiday')}
                className={clsx(
                  "p-3 rounded-lg border transition-colors text-left",
                  operationMode === 'holiday'
                    ? "bg-white border-purple-500 text-purple-700"
                    : "bg-white border-gray-200 hover:border-gray-300"
                )}
              >
                <div className="font-medium">{t('timeSlots.holidayMode')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('timeSlots.holidayModeDesc')}</div>
              </button>

              <button
                onClick={() => setOperationMode('maintenance')}
                className={clsx(
                  "p-3 rounded-lg border transition-colors text-left",
                  operationMode === 'maintenance'
                    ? "bg-white border-orange-500 text-orange-700"
                    : "bg-white border-gray-200 hover:border-gray-300"
                )}
              >
                <div className="font-medium">{t('timeSlots.maintenanceMode')}</div>
                <div className="text-xs text-gray-500 mt-1">{t('timeSlots.maintenanceModeDesc')}</div>
              </button>
            </div>

            {/* 操作表单 */}
            {operationMode !== 'view' && (
              <div className="bg-white rounded-lg p-4 border">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('timeSlots.reason')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={quickActionData.reason}
                      onChange={(e) => setQuickActionData(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder={t('timeSlots.reasonPlaceholder')}
                      className="w-full input"
                    />
                  </div>

                  {(operationMode === 'holiday' || operationMode === 'maintenance') && (
                    <>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('timeSlots.startDate')}
                          </label>
                          <input
                            type="date"
                            value={quickActionData.startDate}
                            onChange={(e) => setQuickActionData(prev => ({ ...prev, startDate: e.target.value }))}
                            className="w-full input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('timeSlots.endDate')}
                          </label>
                          <input
                            type="date"
                            value={quickActionData.endDate}
                            onChange={(e) => setQuickActionData(prev => ({ ...prev, endDate: e.target.value }))}
                            className="w-full input"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('timeSlots.startTime')}
                          </label>
                          <input
                            type="time"
                            value={quickActionData.startTime}
                            onChange={(e) => setQuickActionData(prev => ({ ...prev, startTime: e.target.value }))}
                            className="w-full input"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('timeSlots.endTime')}
                          </label>
                          <input
                            type="time"
                            value={quickActionData.endTime}
                            onChange={(e) => setQuickActionData(prev => ({ ...prev, endTime: e.target.value }))}
                            className="w-full input"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setOperationMode('view')}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={executeQuickAction}
                    className="btn btn-primary gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    {t('timeSlots.executeOperation')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 日期选择和时间段列表 */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  {t('timeSlots.dailySchedule')}
                </h4>

                <div className="flex items-center gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('timeSlots.selectDate')}
                    </label>
                    <input
                      type="date"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      className="input"
                    />
                  </div>

                  <button
                    onClick={fetchTimeSlots}
                    disabled={isLoading}
                    className="btn btn-secondary gap-2 mt-6"
                  >
                    <RefreshCw className={clsx("h-4 w-4", isLoading && "animate-spin")} />
                    {t('common.refresh')}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6">
              {isLoading ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary-600 mb-4" />
                  <p className="text-gray-600">{t('timeSlots.loading')}</p>
                </div>
              ) : timeSlots.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {timeSlots.map((timeSlot) => (
                    <div
                      key={timeSlot.id}
                      className="p-4 border border-gray-200 rounded-lg hover:shadow-medium transition-shadow"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-medium text-gray-900">
                          {timeSlot.start_time} - {timeSlot.end_time}
                        </div>
                        <button
                          onClick={() => toggleTimeSlotStatus(timeSlot)}
                          className={clsx(
                            "px-2 py-1 text-xs font-medium rounded-full cursor-pointer transition-colors",
                            getStatusColor(timeSlot.status),
                            "hover:opacity-80"
                          )}
                        >
                          {t(`timeSlots.status.${timeSlot.status}`)}
                        </button>
                      </div>

                      <div className="space-y-2 text-sm text-gray-600">
                        <div className="flex items-center justify-between">
                          <span>{t('timeSlots.basePrice')}</span>
                          <span className="font-medium">${timeSlot.base_price}</span>
                        </div>
                        {timeSlot.member_price && (
                          <div className="flex items-center justify-between">
                            <span>{t('timeSlots.memberPrice')}</span>
                            <span className="font-medium">${timeSlot.member_price}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span>{t('timeSlots.capacity')}</span>
                          <span className="font-medium">{timeSlot.max_capacity}</span>
                        </div>
                      </div>

                      {timeSlot.unavailable_reason && (
                        <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-600">
                          <div className="font-medium mb-1">{t('timeSlots.reason')}:</div>
                          <div>{timeSlot.unavailable_reason}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">
                    {t('timeSlots.noTimeSlots')}
                  </h4>
                  <p className="text-gray-600">
                    {t('timeSlots.noTimeSlotsDesc')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CourtManagement;
